import { Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AnnouncementOverlay from '../components/AnnouncementOverlay';
import { getDeviceId } from '../utils/deviceId';
import AttendanceFilter from '../components/AttendanceFilter';
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box, Typography, Alert, Chip, Button, CircularProgress,
  Table, TableBody, TableCell, TableHead, TableRow, Paper,
  Card, CardContent, Divider, TextField
} from '@mui/material';

import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import Sidebar from '../components/Sidebar';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import { useGeolocation } from '../hooks/useGeolocation';
import { useLiveTimer } from '../hooks/useLiveTimer';


import { SIDEBAR_WIDTH } from '../components/Sidebar';
import { useIsMobile } from '../hooks/useIsMobile';

export default function Dashboard() {
  const { user, refreshUser } = useAuth();
  const isMobile = useIsMobile();
  const location = useLocation();
  const [earlyClockoutDialog, setEarlyClockoutDialog] = useState(false);
  const [earlyReason, setEarlyReason] = useState('');
  const tab = new URLSearchParams(location.search).get('tab') || 'dashboard';
  const { getLocation, loading: geoLoading } = useGeolocation();
  const [activeSession, setActiveSession] = useState(null);
  const [records, setRecords] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const timer = useLiveTimer(activeSession?.clock_in);

  useEffect(() => {
    Promise.all([fetchActive(), fetchRecords()]).finally(() => setPageLoading(false));
  }, []);

  const fetchActive = async () => {
    const { data } = await api.get('/attendance/active/');
    setActiveSession(data);
  };

  const fetchRecords = async () => {
    const { data } = await api.get('/attendance/my/');
    setRecords(Array.isArray(data) ? data : data.results || []);
  };

  const handleFilter = async (params) => {
  try {
    const query = new URLSearchParams(params).toString();
    const { data } = await api.get(`/attendance/my/${query ? '?' + query : ''}`);
    setRecords(Array.isArray(data) ? data : data.results || []);
  } catch (err) {
    setError('Failed to apply filters. Please try again.');
  }
};

  const handleClockIn = async () => {
  setError(''); setSuccess('');
  setActionLoading(true);
  try {
    const coords = await getLocation();
    const deviceId = await getDeviceId();
    const { data } = await api.post('/attendance/clock-in/', {
      ...coords, device_id: deviceId,
    });
    setActiveSession(data);
    if (data.lateness_warning) {
      setSuccess(`Clocked in successfully. ⚠️ ${data.lateness_warning}`);
    } else {
      setSuccess('You have successfully clocked in.');
    }
    fetchRecords();
  } catch (err) {
    const errData = err.response?.data;
    if (errData?.after_closing) {
      setError(errData.error);
    } else {
      setError(errData?.error || err.toString());
    }
  } finally {
    setActionLoading(false);
  }
};



  const handleClockOut = async (earlyReason = '') => {
  setError(''); setSuccess('');
  setActionLoading(true);
  try {
    const coords = await getLocation();
    const deviceId = await getDeviceId();
    const { data } = await api.post('/attendance/clock-out/', {
      ...coords,
      device_id: deviceId,
      early_reason: earlyReason,
    });
    setActiveSession(null);
    if (data.early_warning) {
      setSuccess(`Clocked out. ⚠️ ${data.early_warning}`);
    } else {
      setSuccess('You have successfully clocked out.');
    }
    fetchRecords();
  } catch (err) {
    const errData = err.response?.data;
    if (errData?.requires_reason) {
      setEarlyClockoutDialog(true);
    } else {
      setError(errData?.error || err.toString());
    }
  } finally {
    setActionLoading(false);
  }
};

  const today = new Date().toLocaleDateString('en-GB', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
  });

  const totalHoursToday = records
    .filter(r => new Date(r.clock_in).toDateString() === new Date().toDateString() && r.duration_seconds)
    .reduce((sum, r) => sum + r.duration_seconds, 0);

  const formatHours = (secs) => {
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    return `${h}h ${m}m`;
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', background: '#f4f6f4', overflowX: 'hidden' }}>
    <AnnouncementOverlay />
    <Sidebar />
    <Box sx={{
  ml: isMobile ? 0 : `${SIDEBAR_WIDTH}px`,
  p: isMobile ? 2 : 3,
  pt: isMobile ? 7 : 3,
  minHeight: '100vh',
  flex: 1,
  minWidth: 0,
  overflowX: 'hidden',
}}>

        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 500 }}>
              {tab === 'attendance' ? 'My Attendance' : tab === 'profile' ? 'My Profile' : `${new Date().getHours() < 12 ? 'Good morning' : 'Good afternoon'}, ${user?.first_name || user?.username}`}
            </Typography>
            <Typography sx={{ color: '#888', fontSize: 13, mt: 0.5 }}>{today}</Typography>
          </Box>
          <Chip
            label={activeSession ? 'Clocked In' : 'Not Clocked In'}
            sx={{
              background: activeSession ? '#eaf3de' : '#f1efe8',
              color: activeSession ? '#27500a' : '#444',
              fontWeight: 500,
              fontSize: 12,
            }}
          />
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

        {/* Dashboard tab */}
        {tab === 'dashboard' && (
          <>
            {/* Stat cards */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 2, mb: 3 }}>
              <Card>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Typography sx={{ fontSize: 11, color: '#888', mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Status today
                  </Typography>
                  <Typography sx={{ fontSize: 20, fontWeight: 500, color: activeSession ? '#1a4d2e' : '#888' }}>
                    {activeSession ? 'Present' : 'Not in yet'}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: '#aaa', mt: 0.5 }}>
                    {new Date().toLocaleDateString()}
                  </Typography>
                </CardContent>
              </Card>
              <Card>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Typography sx={{ fontSize: 11, color: '#888', mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Hours today
                  </Typography>
                  <Typography sx={{ fontSize: 20, fontWeight: 500, color: '#1a4d2e' }}>
                    {activeSession ? timer : totalHoursToday > 0 ? formatHours(totalHoursToday) : '0h 0m'}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: '#aaa', mt: 0.5 }}>
                    {activeSession ? 'Currently active' : 'Total logged'}
                  </Typography>
                </CardContent>
              </Card>
              <Card>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Typography sx={{ fontSize: 11, color: '#888', mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Total records
                  </Typography>
                  <Typography sx={{ fontSize: 20, fontWeight: 500 }}>
                    {records.length}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: '#aaa', mt: 0.5 }}>All time</Typography>
                </CardContent>
              </Card>
            </Box>

            {/* Clock in/out card */}
            <Card sx={{ mb: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      {activeSession
                        ? <CheckCircleOutlineIcon sx={{ color: '#2d7a4f', fontSize: 18 }} />
                        : <RadioButtonUncheckedIcon sx={{ color: '#bbb', fontSize: 18 }} />}
                      <Typography sx={{ fontSize: 13, color: activeSession ? '#2d7a4f' : '#888', fontWeight: 500 }}>
                        {activeSession ? 'Currently clocked in' : 'Not clocked in'}
                      </Typography>
                    </Box>
                    {activeSession ? (
                      <>
                        <Typography sx={{ fontSize: 40, fontWeight: 500, color: '#1a4d2e', fontVariantNumeric: 'tabular-nums', lineHeight: 1.1 }}>
                          {timer}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: '#888', mt: 0.5 }}>
                          Since {new Date(activeSession.clock_in).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </Typography>
                      </>
                    ) : (
                      <Typography sx={{ fontSize: 14, color: '#666', mt: 1 }}>
                        Click the button to clock in for today
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
                    {activeSession ? (
                      <Button
                        variant="outlined"
                        color="error"
                        size="large"
                        startIcon={actionLoading || geoLoading ? <CircularProgress size={16} /> : <AccessTimeIcon />}
                        onClick={() => handleClockOut('')}
                        disabled={actionLoading || geoLoading}
                        sx={{ minWidth: 140, borderRadius: 2 }}
                      >
                        Clock Out
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        size="large"
                        startIcon={actionLoading || geoLoading ? <CircularProgress size={16} color="inherit" /> : <AccessTimeIcon />}
                        onClick={handleClockIn}
                        disabled={actionLoading || geoLoading}
                        sx={{ minWidth: 140, background: '#1a4d2e', '&:hover': { background: '#2d7a4f' }, borderRadius: 2 }}
                      >
                        Clock In
                      </Button>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <LocationOnIcon sx={{ fontSize: 13, color: '#aaa' }} />
                      <Typography sx={{ fontSize: 11, color: '#aaa' }}>Location required</Typography>
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            {/* Recent records */}
            <Card>
              <Box sx={{ px: 2.5, py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '0.5px solid #e8ede8' }}>
                <Typography sx={{ fontSize: 14, fontWeight: 500 }}>Recent attendance</Typography>
                <Typography
                  sx={{ fontSize: 12, color: '#2d7a4f', cursor: 'pointer' }}
                  onClick={() => window.location.href = '/dashboard?tab=attendance'}
                >
                  View all →
                </Typography>
              </Box>
              <AttendanceTable records={records.slice(0, 5)} />
            </Card>
          </>
        )}

        {/* Attendance tab */}
        {tab === 'attendance' && (
          <Card>
            <Box sx={{
              px: 2.5, py: 1.5,
              borderBottom: '0.5px solid #e8ede8',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <Typography sx={{ fontSize: 14, fontWeight: 500 }}>My attendance records</Typography>
              <Typography sx={{ fontSize: 12, color: '#888' }}>{records.length} record{records.length !== 1 ? 's' : ''}</Typography>
            </Box>
            <AttendanceFilter onFilter={handleFilter} />
            <AttendanceTable records={records} />
         </Card>
        )}

        {/* Profile tab */}
        {tab === 'profile' && (
         <ProfileTab user={user} refreshUser={refreshUser} />
        )}

        {/* Early clock-out reason dialog */}
<Dialog
  open={earlyClockoutDialog}
  onClose={() => setEarlyClockoutDialog(false)}
  maxWidth="xs"
  fullWidth
  fullScreen={isMobile}
>
  <DialogTitle sx={{ fontWeight: 500, display: 'flex', alignItems: 'center', gap: 1 }}>
    <WarningAmberIcon sx={{ color: '#854f0b', fontSize: 20 }} />
    Early clock-out
  </DialogTitle>
  <DialogContent>
    <Typography sx={{ fontSize: 13, color: '#666', mb: 2 }}>
      You are clocking out before 4:00 PM. Please provide a reason.
      This will be sent to your department head for approval.
    </Typography>
    <TextField
      label="Reason for early clock-out"
      fullWidth
      multiline
      rows={3}
      value={earlyReason}
      onChange={(e) => setEarlyReason(e.target.value)}
      placeholder="e.g. Medical appointment, family emergency..."
    />
  </DialogContent>
  <DialogActions sx={{ px: 3, pb: 2 }}>
    <Button
      onClick={() => setEarlyClockoutDialog(false)}
      sx={{ color: '#888' }}
    >
      Cancel
    </Button>
    <Button
      variant="contained"
      disabled={!earlyReason.trim()}
      onClick={() => {
        setEarlyClockoutDialog(false);
        handleClockOut(earlyReason);
        setEarlyReason('');
      }}
      sx={{ background: '#1a4d2e', '&:hover': { background: '#2d7a4f' } }}
    >
      Submit and clock out
    </Button>
  </DialogActions>
</Dialog>
      </Box>
    </Box>
  );
}

function AttendanceTable({ records }) {
  return (
    <Box sx={{ overflowX: 'auto' }}>
  <Table size="small" sx={{ minWidth: 500 }}>
      <TableHead>
        <TableRow>
          <TableCell>Date</TableCell>
          <TableCell>Clock In</TableCell>
          <TableCell>Clock Out</TableCell>
          <TableCell>Duration</TableCell>
          <TableCell>Status</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {records.map((r) => (
          <TableRow key={r.id} sx={{ '&:last-child td': { border: 0 } }}>
            <TableCell>{new Date(r.clock_in).toLocaleDateString()}</TableCell>
            <TableCell>{new Date(r.clock_in).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</TableCell>
            <TableCell>{r.clock_out ? new Date(r.clock_out).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—'}</TableCell>
            <TableCell>{r.duration_display || '—'}</TableCell>
            <TableCell>
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
    <Chip
      label={r.status}
      size="small"
      sx={{
        background: r.status === 'active' ? '#eaf3de' : '#f1efe8',
        color: r.status === 'active' ? '#27500a' : '#444',
        fontSize: 11,
      }}
    />
    {r.is_late && (
      <Chip
        label={`Late ${r.lateness_minutes}m`}
        size="small"
        sx={{ background: '#faeeda', color: '#633806', fontSize: 11 }}
      />
    )}
    {r.is_early_clockout && (
      <Chip
        label="Early out"
        size="small"
        sx={{ background: '#fcebeb', color: '#791f1f', fontSize: 11 }}
      />
    )}
    
  </Box>
</TableCell>
          </TableRow>
        ))}
        {records.length === 0 && (
          <TableRow>
            <TableCell colSpan={6} align="center" sx={{ py: 4, color: '#aaa', fontSize: 13 }}>
              No attendance records found
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  </Box>
  );
}
function ProfileTab({ user, refreshUser }) {
  const [photoLoading, setPhotoLoading] = useState(false);
  const [pwForm, setPwForm] = useState({
    current_password: '', new_password: '', confirm_password: ''
  });
  const [pwError, setPwError] = useState('');
  const [pwSuccess, setPwSuccess] = useState('');
  const [pwLoading, setPwLoading] = useState(false);
  const [photoError, setPhotoError] = useState('');
  const fileInputRef = React.useRef();

  const [mfaLoading, setMfaLoading] = useState(false);

const handleToggleMFA = async () => {
  if (!user?.email) {
    alert('You need an email address to enable MFA. Contact your administrator.');
    return;
  }
  setMfaLoading(true);
  try {
    const { data } = await api.post('/auth/toggle-mfa/');
    await refreshUser();
    alert(data.message);
  } catch (err) {
    alert(err.response?.data?.error || 'Failed to toggle MFA.');
  } finally {
    setMfaLoading(false);
  }
};

  const handlePhotoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setPhotoError('Photo must be under 5MB.');
      return;
    }
    setPhotoLoading(true);
    setPhotoError('');
    const formData = new FormData();
    formData.append('profile_photo', file);
    try {
      await api.post('/auth/upload-photo/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      await refreshUser();
    } catch {
      setPhotoError('Photo upload failed. Please try again.');
    } finally {
      setPhotoLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPwError(''); setPwSuccess('');
    if (pwForm.new_password !== pwForm.confirm_password) {
      setPwError('New passwords do not match.');
      return;
    }
    setPwLoading(true);
    try {
      await api.post('/auth/change-password/', pwForm);
      setPwSuccess('Password changed successfully.');
      setPwForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      setPwError(err.response?.data?.error || 'Password change failed.');
    } finally {
      setPwLoading(false);
    }
  };

  const initials = `${user?.first_name?.[0] || ''}${user?.last_name?.[0] || ''}`.toUpperCase()
    || user?.username?.[0]?.toUpperCase();

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: { xs: '1fr', md: '1fr 1fr' } }, gap: 3, maxWidth: 860 }}>

      {/* Profile info card */}
      <Card>
        <Box sx={{ px: 2.5, py: 1.5, borderBottom: '0.5px solid #e8ede8' }}>
          <Typography sx={{ fontSize: 14, fontWeight: 500 }}>Profile information</Typography>
        </Box>
        <CardContent sx={{ p: 3 }}>

          {/* Avatar */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
            <Box sx={{ position: 'relative', mb: 1.5 }}>
              {user?.profile_photo_url ? (
                <Box
                  component="img"
                  src={user.profile_photo_url}
                  alt="Profile"
                  sx={{ width: 88, height: 88, borderRadius: '50%', objectFit: 'cover', border: '3px solid #eaf3de' }}
                />
              ) : (
                <Box sx={{
                  width: 88, height: 88, borderRadius: '50%',
                  background: '#eaf3de', display: 'flex',
                  alignItems: 'center', justifyContent: 'center',
                  fontSize: 28, fontWeight: 500, color: '#1a4d2e',
                  border: '3px solid #d0e8d0',
                }}>
                  {initials}
                </Box>
              )}
            </Box>

            <input
              type="file"
              accept="image/*"
              ref={fileInputRef}
              style={{ display: 'none' }}
              onChange={handlePhotoChange}
            />

            <Button
              size="small"
              variant="outlined"
              onClick={() => fileInputRef.current.click()}
              disabled={photoLoading}
              sx={{ fontSize: 12, borderColor: '#2d7a4f', color: '#1a4d2e', borderRadius: 2 }}
            >
              {photoLoading ? <CircularProgress size={14} /> : 'Change photo'}
            </Button>

            {photoError && (
              <Typography sx={{ fontSize: 12, color: 'red', mt: 1 }}>{photoError}</Typography>
            )}
            <Typography sx={{ fontSize: 11, color: '#bbb', mt: 0.5 }}>
              JPG or PNG, max 5MB
            </Typography>
          </Box>

          <Divider sx={{ mb: 2 }} />

          {/* Profile fields */}
          {[
            { label: 'Full name', value: `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || '—' },
            { label: 'Username', value: `@${user?.username}` },
            { label: 'Phone', value: user?.phone || '—' },
            { label: 'Email', value: user?.email || '—' },
            { label: 'Department', value: user?.department || '—' },
            { label: 'State of assignment', value: user?.state_of_assignment || '—' },
            { label: 'Phone', value: user?.phone || '—' },
            { label: 'Role', value: user?.role },
          ].map(({ label, value }) => (
            <Box key={label} sx={{
              display: 'flex', justifyContent: 'space-between',
              py: 1, borderBottom: '0.5px solid #f0f4f0'
            }}>
              <Typography sx={{ fontSize: 13, color: '#888' }}>{label}</Typography>
              <Typography sx={{ fontSize: 13, fontWeight: 500, textAlign: 'right', maxWidth: '60%' }}>
                {value}
              </Typography>
            </Box>
          ))}

          {/* MFA Toggle */}
<Box sx={{ mt: 2, p: 2, background: '#fafcfa', borderRadius: 2, border: '0.5px solid #e0e8e0' }}>
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
    <Box>
      <Typography sx={{ fontSize: 13, fontWeight: 500 }}>
        Two-Factor Authentication (MFA)
      </Typography>
      <Typography sx={{ fontSize: 12, color: '#888', mt: 0.5 }}>
        {user?.mfa_enabled
          ? 'MFA is enabled. An OTP will be sent to your email on each login.'
          : 'MFA is disabled. Enable it for extra security.'}
      </Typography>
    </Box>
    <Button
      size="small"
      variant={user?.mfa_enabled ? 'outlined' : 'contained'}
      onClick={handleToggleMFA}
      disabled={mfaLoading}
      sx={user?.mfa_enabled
        ? { borderColor: '#791f1f', color: '#791f1f', borderRadius: 2, fontSize: 12 }
        : { background: '#1a4d2e', '&:hover': { background: '#2d7a4f' }, borderRadius: 2, fontSize: 12 }
      }
    >
      {mfaLoading ? <CircularProgress size={14} color="inherit" /> : user?.mfa_enabled ? 'Disable MFA' : 'Enable MFA'}
    </Button>
  </Box>
</Box>
        </CardContent>
      </Card>

      {/* Change password card */}
      <Card sx={{ alignSelf: 'start' }}>
        <Box sx={{ px: 2.5, py: 1.5, borderBottom: '0.5px solid #e8ede8' }}>
          <Typography sx={{ fontSize: 14, fontWeight: 500 }}>Change password</Typography>
        </Box>
        <CardContent sx={{ p: 3 }}>
          {pwError && (
            <Alert severity="error" sx={{ mb: 2, fontSize: 13, borderRadius: 2 }}>
              {pwError}
            </Alert>
          )}
          {pwSuccess && (
            <Alert severity="success" sx={{ mb: 2, fontSize: 13, borderRadius: 2 }}>
              {pwSuccess}
            </Alert>
          )}
          <form onSubmit={handlePasswordChange}>
            {[
              { field: 'current_password', label: 'Current password' },
              { field: 'new_password', label: 'New password' },
              { field: 'confirm_password', label: 'Confirm new password' },
            ].map(({ field, label }) => (
              <TextField
                key={field}
                label={label}
                type="password"
                fullWidth
                margin="dense"
                required
                value={pwForm[field]}
                onChange={(e) => setPwForm({ ...pwForm, [field]: e.target.value })}
              />
            ))}
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={pwLoading}
              sx={{
                mt: 2, background: '#1a4d2e',
                '&:hover': { background: '#2d7a4f' },
                borderRadius: 2,
              }}
            >
              {pwLoading ? <CircularProgress size={18} color="inherit" /> : 'Update password'}
            </Button>
          </form>

          <Box sx={{ mt: 2.5, p: 1.5, background: '#fafcfa', borderRadius: 2, border: '0.5px solid #e0e8e0' }}>
            <Typography sx={{ fontSize: 12, color: '#888', mb: 0.5, fontWeight: 500 }}>
              Password requirements
            </Typography>
            {['At least 8 characters', 'Mix of letters and numbers recommended'].map((req) => (
              <Typography key={req} sx={{ fontSize: 12, color: '#aaa' }}>• {req}</Typography>
            ))}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}